import React, { useState, useEffect, useCallback } from 'react';
import Cookies from 'js-cookie';
import CloseIcon from '@mui/icons-material/Close';
import { SidebarData } from './Sidebar/SidebarData';
import { updatePermissionCache } from '../utils/auth';

const API_BASE = 'https://system.sales-management.cloud/api/permissions.php';

// SidebarData から permissionKey を抽出
function extractPermissionKeys() {
    const keys = new Set();
    
    const addKey = (item) => {
        if (item.permissionKey) {
            keys.add(item.permissionKey);
        }
        if (item.submenu) {
            item.submenu.forEach(addKey);
        }
    };
    
    SidebarData.forEach(addKey);
    return Array.from(keys).sort();
}

// permissionKey の表示ラベル
function getPermissionLabel(key) {
    const findLabel = (items) => {
        for (const item of items) {
            if (item.permissionKey === key) {
                return item.title;
            }
            if (item.submenu) {
                const submenuLabel = findLabel(item.submenu);
                if (submenuLabel) return submenuLabel;
            }
        }
        return null;
    };
    
    return findLabel(SidebarData) || key;
}

// permissionKey から module/action に変換するマッピング
const permissionKeyToModuleAction = {
    'dashboard': [['ui', 'dashboard']],
    'report_view': [['reports', 'view']],
    'report_admin': [['reports', 'admin'], ['reports', 'edit'], ['reports', 'create']],
    'sales_view': [['sales', 'view']],
    'event_view': [['events', 'view']],
    'institution_view': [['facilities', 'view']],
    'shift_view': [['shifts', 'view']],
    'shift_admin': [['shifts', 'admin'], ['shifts', 'edit'], ['shifts', 'create']],
    'analytics_view': [['analytics', 'view']],
    'analytics_admin': [['analytics', 'admin']],
    'invoice_view': [['invoices', 'view']],
    'invoice_admin': [['invoices', 'admin'], ['invoices', 'edit'], ['invoices', 'create']],
    'notices_view': [['notifications', 'view']],
    'notices_admin': [['notifications', 'admin'], ['notifications', 'edit'], ['notifications', 'create'], ['notifications', 'delete']],
    'tests_view': [['tests', 'view']],
    'tests_admin': [['tests', 'admin']],
    'learning_view': [['materials', 'view'], ['learning', 'view']],
    'learning_admin': [['materials', 'admin'], ['materials', 'edit'], ['materials', 'create']],
    'attendance_admin': [['attendance', 'admin']],
    'users_admin': [['users', 'admin'], ['users', 'manage_permissions'], ['users', 'manage_roles']]
};

function normalizeLoadedPermissionKeys(rawPermissionKeys = []) {
    const normalizedKeys = new Set(rawPermissionKeys.filter((key) => permissionKeyToModuleAction[key]));
    const moduleActionSet = new Set(rawPermissionKeys.filter((key) => typeof key === 'string' && key.includes('/')));

    if (moduleActionSet.size > 0) {
        Object.entries(permissionKeyToModuleAction).forEach(([permissionKey, requiredPairs]) => {
            const hasAllPairs = requiredPairs.every(([module, action]) => {
                return moduleActionSet.has(`${module}/${action}`);
            });

            if (hasAllPairs) {
                normalizedKeys.add(permissionKey);
            }
        });
    }

    return Array.from(normalizedKeys);
}

function PermissionsPanel({ userId, userName, onClose }) {
    const [permissions, setPermissions] = useState(new Set());
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    const permissionKeys = extractPermissionKeys();

    const loadPermissions = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const token = Cookies.get('auth_token');
            
            if (!token) {
                setError('認証情報が見つかりません');
                return;
            }

            const response = await fetch(
                `${API_BASE}?action=get_user_permissions&user_id=${userId}`,
                {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(`API Error ${response.status}: ${data.message || 'Unknown error'}`);
            }

            if (data.status === 'success') {
                const rawPermissionKeys = (data.data.permissions || []).map((permission) => permission.permission_key);
                const normalizedPermissionKeys = normalizeLoadedPermissionKeys(rawPermissionKeys);
                const perms = new Set(normalizedPermissionKeys);
                setPermissions(perms);
                // キャッシュも更新
                updatePermissionCache(Array.from(perms));
            } else {
                setError(data.message || '権限の取得に失敗しました');
            }
        } catch (error) {
            console.error('Permission load error:', error);
            setError(`エラー: ${error.message}`);
        } finally {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        loadPermissions();
    }, [loadPermissions, userId]);

    const togglePermission = (permissionKey) => {
        setPermissions(prev => {
            const newPerms = new Set(prev);
            if (newPerms.has(permissionKey)) {
                newPerms.delete(permissionKey);
            } else {
                newPerms.add(permissionKey);
            }
            return newPerms;
        });
    };

    const savePermissions = async () => {
        try {
            setSaving(true);
            setError('');
            setSuccess('');
            
            const token = Cookies.get('auth_token');
            if (!token) {
                setError('認証情報が見つかりません');
                return;
            }

            // permissionKey を module/action に変換
            const moduleActions = new Set();
            Array.from(permissions).forEach(key => {
                const actions = permissionKeyToModuleAction[key];
                if (actions) {
                    actions.forEach(([module, action]) => {
                        moduleActions.add(JSON.stringify([module, action]));
                    });
                }
            });

            const permissionsList = Array.from(moduleActions).map(item => {
                const [module, action] = JSON.parse(item);
                return { module, action };
            });

            const selectedPermissionKeys = Array.from(permissions);

            const response = await fetch(
                `${API_BASE}?action=grant_bulk_permissions`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        user_id: userId,
                        permission_keys: selectedPermissionKeys,
                        module_permissions: permissionsList
                    })
                }
            );

            if (!response.ok) {
                throw new Error(`API Error: ${response.status}`);
            }

            const data = await response.json();

            if (data.status === 'success') {
                // キャッシュも更新
                updatePermissionCache(Array.from(permissions));
                
                setSuccess('権限を保存しました');
                setTimeout(() => setSuccess(''), 3000);
            } else {
                setError(data.message || '権限の保存に失敗しました');
            }
        } catch (error) {
            console.error('Permission save error:', error);
            setError(`エラー: ${error.message}`);
        } finally {
            setSaving(false);
        }
    };

    const hasPermission = (key) => {
        return permissions.has(key);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                {/* ヘッダー */}
                <div className="sticky top-0 px-6 py-4 border-b border-gray-200 bg-white flex justify-between items-center">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">権限管理</h2>
                        <p className="text-sm text-gray-600 mt-1">{userName}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 p-2"
                    >
                        <CloseIcon />
                    </button>
                </div>

                {/* エラー/成功メッセージ */}
                {error && (
                    <div className="m-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded text-sm">
                        {error}
                    </div>
                )}
                {success && (
                    <div className="m-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded text-sm">
                        {success}
                    </div>
                )}

                {/* コンテンツ */}
                {loading ? (
                    <div className="p-6 text-center text-gray-500">
                        読み込み中...
                    </div>
                ) : (
                    <>
                        {/* 権限リスト */}
                        <div className="p-6 space-y-3">
                            {permissionKeys.length === 0 ? (
                                <p className="text-gray-500 text-center py-4">権限がありません</p>
                            ) : (
                                permissionKeys.map(key => (
                                    <label key={key} className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors">
                                        <input
                                            type="checkbox"
                                            checked={hasPermission(key)}
                                            onChange={() => togglePermission(key)}
                                            className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
                                        />
                                        <span className="ml-3 text-gray-700 font-medium">
                                            {getPermissionLabel(key)}
                                        </span>
                                    </label>
                                ))
                            )}
                        </div>

                        {/* フッター（保存ボタン） */}
                        <div className="sticky bottom-0 px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
                            <button
                                onClick={onClose}
                                disabled={saving}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 transition-colors"
                            >
                                キャンセル
                            </button>
                            <button
                                onClick={savePermissions}
                                disabled={saving}
                                className="px-4 py-2 text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
                            >
                                {saving ? '保存中...' : '保存'}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}


export default PermissionsPanel;
